import type { InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes, ReactNode } from "react";

const CONTROL =
  "block w-full rounded-lg border border-hairline bg-paper-raised px-4 py-3 text-base text-ink placeholder:text-slate/60 " +
  "focus:border-accent-ink focus:outline-none focus:ring-2 focus:ring-accent-ink/20 disabled:opacity-60";

function Field({ id, label, hint, error, required, children }: { id: string; label: string; hint?: string; error?: string; required?: boolean; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-slate-strong">
        {label}{required ? <span aria-hidden="true" className="text-signal-ink"> *</span> : null}
      </label>
      {children}
      {hint && !error ? <p id={`${id}-hint`} className="mt-1.5 text-xs text-slate">{hint}</p> : null}
      {error ? <p id={`${id}-error`} role="alert" className="mt-1.5 text-xs font-medium text-red-700">{error}</p> : null}
    </div>
  );
}

type Common = { id: string; label: string; hint?: string; error?: string };

export function Input({ id, label, hint, error, className = "", ...rest }: Common & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <Field id={id} label={label} hint={hint} error={error} required={rest.required}>
      <input id={id} name={rest.name ?? id} aria-invalid={!!error} aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined} className={`${CONTROL} ${className}`} {...rest} />
    </Field>
  );
}

/** Links the textarea to its error or hint text via aria-describedby, matching Input's a11y wiring. */
export function Textarea({ id, label, hint, error, className = "", ...rest }: Common & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <Field id={id} label={label} hint={hint} error={error} required={rest.required}>
      <textarea id={id} name={rest.name ?? id} rows={rest.rows ?? 4} aria-invalid={!!error} aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined} className={`${CONTROL} ${className}`} {...rest} />
    </Field>
  );
}

export function Select({ id, label, hint, error, options, className = "", ...rest }: Common & { options: { value: string; label: string }[] } & SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <Field id={id} label={label} hint={hint} error={error} required={rest.required}>
      <select id={id} name={rest.name ?? id} aria-invalid={!!error} className={`${CONTROL} ${className}`} {...rest}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Field>
  );
}

/** Bots fill every field; people never see this one. Server rejects when non-empty. */
export function HoneypotField({ name = "website" }: { name?: string }) {
  return (
    <div aria-hidden="true" className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden">
      <label htmlFor={`hp-${name}`}>Website</label>
      <input id={`hp-${name}`} name={name} type="text" tabIndex={-1} autoComplete="off" defaultValue="" />
    </div>
  );
}

export function FormStatus({ state, success, error }: { state: "idle" | "submitting" | "success" | "error"; success: string; error?: string }) {
  if (state === "success") return <p role="status" className="rounded-lg bg-signal-soft px-4 py-3 text-sm font-medium text-signal-ink">{success}</p>;
  if (state === "error") return <p role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{error ?? "Something went wrong. Please try again."}</p>;
  return null;
}
